import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../api';

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'job', label: 'Jobs (collections)' },
  { value: 'client', label: 'Clients' },
  { value: 'invoice', label: 'Invoices' },
];

function shortClass(t) {
  if (!t) return '';
  const parts = String(t).split('\\');
  return parts[parts.length - 1] || t;
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const qs = new URLSearchParams({ page: String(page), per_page: '25' });
        if (type) qs.set('type', type);
        const res = await fetch(`${API_BASE_URL}audit-logs?${qs}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || data.message || `HTTP ${res.status}`);
        }
        if (cancelled) return;
        const dataRows = Array.isArray(data?.data) ? data.data : [];
        setRows(dataRows);
        const lp = data?.last_page ?? data?.meta?.last_page;
        setLastPage(typeof lp === 'number' ? lp : 1);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load audit log');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, type]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <HistoryIcon color="primary" fontSize="large" />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Audit log
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recent creates, updates, and deletes on jobs, clients, and invoices.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="audit-type-label">Entity</InputLabel>
          <Select
            labelId="audit-type-label"
            label="Entity"
            value={type}
            onChange={(e) => {
              setPage(1);
              setType(e.target.value);
            }}
          >
            {TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{r.user?.name || r.user?.email || '—'}</TableCell>
                    <TableCell>{r.action}</TableCell>
                    <TableCell>{shortClass(r.auditable_type)}</TableCell>
                    <TableCell>{r.auditable_id}</TableCell>
                    <TableCell>{r.ip_address || '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setSelected(r);
                          setDetailsOpen(true);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No entries yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {lastPage > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                color="primary"
                page={page}
                count={lastPage}
                onChange={(_, p) => setPage(p)}
              />
            </Box>
          )}
        </>
      )}

      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelected(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit details</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selected?.created_at ? new Date(selected.created_at).toLocaleString() : '—'} ·{' '}
              {selected?.user?.name || selected?.user?.email || '—'} · {selected?.action} ·{' '}
              {shortClass(selected?.auditable_type)} #{selected?.auditable_id}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Old values
              </Typography>
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(selected?.old_values ?? null, null, 2)}
              </pre>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                New values
              </Typography>
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(selected?.new_values ?? null, null, 2)}
              </pre>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDetailsOpen(false);
              setSelected(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
